/**
 * ASIASTYLE-R7.1 / R7.2.2F2 — Home on-site recommendations module (lazy, fail-open).
 */
import { useEffect, useRef, useState } from "react";
import { CrossSellBlock } from "./CrossSellBlock.js";
import type { CrossSellCandidate } from "./cross-sell-rules.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { fetchOnsiteRecommendations, mapOnsiteToCrossSell } from "./onsite-recommendations-v2.js";
import type { CatalogV2Product } from "./catalog-v2-types.js";

export function HomeOnsiteRecommendationsV2({
  lang,
  catalogById,
  onAdd,
  onOpen,
  onRegisterActionable,
  emitEvent,
}: {
  lang: StoreV2Lang;
  catalogById: Map<string, CatalogV2Product>;
  onAdd: (product: CatalogV2Product) => void;
  onOpen: (product: CatalogV2Product) => void;
  onRegisterActionable?: (products: CatalogV2Product[]) => void;
  emitEvent: (name: string, payload: Record<string, unknown>) => void;
}) {
  const [items, setItems] = useState<CrossSellCandidate[]>([]);
  const [title, setTitle] = useState<string | null>(null);
  const [subtitle, setSubtitle] = useState<string | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void fetchOnsiteRecommendations({ surface: "HOME_FOR_YOU", limit: 6 })
      .then((res) => {
        if (!res.ok || !res.enabled || res.hidden || !res.items?.length) return;
        setTitle(res.title_uk ?? null);
        setSubtitle(res.subtitle_uk ?? null);
        const mapped = mapOnsiteToCrossSell(res.items, catalogById, lang);
        setItems(mapped);
        onRegisterActionable?.(mapped.map((m) => m.product));
      })
      .catch(() => {
        /* fail-open */
      });
  }, [catalogById, lang, onRegisterActionable]);

  if (items.length === 0) return null;

  return (
    <div data-testid="sv2-home-onsite-recs">
      {subtitle ? <p className="sv2-muted text-sm m-0 mb-1">{subtitle}</p> : null}
      <CrossSellBlock
        lang={lang}
        items={items}
        onAdd={onAdd}
        onOpen={onOpen}
        {...(onRegisterActionable ? { onRegisterActionable } : {})}
        variant="pdp"
        {...(title ? { title } : {})}
        placement="HOME_FOR_YOU"
        onImpression={(ids) => {
          emitEvent("recommendation_impression", {
            placement: "HOME_FOR_YOU",
            product_ids: ids,
            channel: "onsite",
            release: "R7.1",
          });
        }}
      />
    </div>
  );
}
